import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const INIT = {
  type: 'call', title: '', description: '',
  lead_id: '', opportunity_id: '', customer_id: '', contact_id: '',
  due_date: '', status: 'pending', assigned_to: '',
};
const LIMIT = 15;
const TYPES = ['call', 'meeting', 'email', 'task', 'follow_up'];
const STATUSES = ['pending', 'completed', 'cancelled'];
const typeIcon = {
  call: '📞', meeting: '🤝', email: '✉️', task: '✅', follow_up: '🔔',
};
const statusColor = { pending: 'warning', completed: 'active', cancelled: 'inactive' };

export default function Activities({ setTitle }) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [filterType, setFilterType]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState(INIT);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [leads, setLeads]       = useState([]);
  const [opps, setOpps]         = useState([]);
  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers]       = useState([]);

  useEffect(() => { setTitle('Activities'); }, [setTitle]);
  useEffect(() => {
    api.get('/leads/all').then(r => setLeads(r.data)).catch(() => {});
    api.get('/customers/all').then(r => setCustomers(r.data)).catch(() => {});
    api.get('/contacts/all').then(r => setContacts(r.data)).catch(() => {});
    api.get('/auth/users').then(r => setUsers(r.data)).catch(() => {});
    api.get('/opportunities?limit=200').then(r => setOpps(r.data.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ search, page, limit: LIMIT });
      if (filterType)   params.set('type', filterType);
      if (filterStatus) params.set('status', filterStatus);
      const { data } = await api.get(`/activities?${params}`);
      setItems(data.data || []); setTotal(data.total || 0);
    } catch { setItems([]); } finally { setLoading(false); }
  }, [search, page, filterType, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(INIT); setEditId(null); setModal(true); };
  const openEdit = (item) => {
    setForm({
      type: item.type, title: item.title, description: item.description || '',
      lead_id: item.lead_id || '', opportunity_id: item.opportunity_id || '',
      customer_id: item.customer_id || '', contact_id: item.contact_id || '',
      due_date: item.due_date ? item.due_date.slice(0, 16) : '',
      status: item.status, assigned_to: item.assigned_to || '',
    });
    setEditId(item.id); setModal(true);
  };
  const closeModal = () => setModal(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) return alert('Title is required');
    setSaving(true);
    try {
      if (editId) await api.put(`/activities/${editId}`, form);
      else        await api.post('/activities', form);
      closeModal(); load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving activity');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete activity "${title}"?`)) return;
    try { await api.delete(`/activities/${id}`); load(); }
    catch (err) { alert(err.response?.data?.error || 'Error deleting'); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Activities</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Activity
        </button>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input placeholder="Search title or description…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <select className="form-control" style={{ width: 140 }} value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }}>
            <option value="">All Types</option>
            {TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
          </select>
          <select className="form-control" style={{ width: 140 }} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} activities</span>
        </div>

        <div className="table-wrap">
          {loading ? <div className="loading">Loading…</div> : (
            <table>
              <thead>
                <tr>
                  <th>#</th><th>Type</th><th>Title</th><th>Linked To</th>
                  <th>Due Date</th><th>Status</th><th>Assigned</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={8} className="empty">No activities found</td></tr>
                ) : items.map((item, i) => {
                  const linkedTo = item.customer_name || item.lead_name || item.opportunity_title || item.contact_name || '—';
                  return (
                    <tr key={item.id}>
                      <td className="text-muted">{(page - 1) * LIMIT + i + 1}</td>
                      <td>
                        <span title={item.type}>{typeIcon[item.type] || '📋'} </span>
                        <span style={{ textTransform: 'capitalize' }}>{item.type.replace('_', ' ')}</span>
                      </td>
                      <td><strong>{item.title}</strong></td>
                      <td className="text-muted">{linkedTo}</td>
                      <td>{item.due_date ? new Date(item.due_date).toLocaleString() : '—'}</td>
                      <td><span className={`badge badge-${statusColor[item.status]}`} style={{ textTransform: 'capitalize' }}>{item.status}</span></td>
                      <td>{item.assigned_name || '—'}</td>
                      <td>
                        <div className="actions">
                          <button className="btn-icon" title="Edit" onClick={() => openEdit(item)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id, item.title)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
            <div className="pagination-btns">
              <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editId ? 'Edit Activity' : 'Add Activity'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Type *</label>
                  <select className="form-control" value={form.type} onChange={e => f('type', e.target.value)}>
                    {TYPES.map(t => <option key={t} value={t}>{typeIcon[t]} {t.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-control" value={form.status} onChange={e => f('status', e.target.value)}>
                    {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group full">
                  <label>Title *</label>
                  <input className="form-control" value={form.title} onChange={e => f('title', e.target.value)} placeholder="Activity title" />
                </div>
                <div className="form-group">
                  <label>Due Date & Time</label>
                  <input type="datetime-local" className="form-control" value={form.due_date} onChange={e => f('due_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Assigned To</label>
                  <select className="form-control" value={form.assigned_to} onChange={e => f('assigned_to', e.target.value)}>
                    <option value="">— Unassigned —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Link to Lead</label>
                  <select className="form-control" value={form.lead_id} onChange={e => f('lead_id', e.target.value)}>
                    <option value="">— None —</option>
                    {leads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Link to Opportunity</label>
                  <select className="form-control" value={form.opportunity_id} onChange={e => f('opportunity_id', e.target.value)}>
                    <option value="">— None —</option>
                    {opps.map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Link to Customer</label>
                  <select className="form-control" value={form.customer_id} onChange={e => f('customer_id', e.target.value)}>
                    <option value="">— None —</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Link to Contact</label>
                  <select className="form-control" value={form.contact_id} onChange={e => f('contact_id', e.target.value)}>
                    <option value="">— None —</option>
                    {contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group full">
                  <label>Description</label>
                  <textarea className="form-control" rows={3} value={form.description} onChange={e => f('description', e.target.value)} placeholder="Details about this activity…" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update Activity' : 'Create Activity'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
